import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  ThemeProvider,
  createTheme,
  CssBaseline,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { Phone } from 'lucide-react';
import { styled } from '@mui/system';
import { useState } from 'react';

// Create theme with Prudential branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc', // Prudential blue
      light: '#4d94ff',
      dark: '#004c99',
    },
    error: {
      main: '#C11919', // Prudential red
      light: '#d45959',
      dark: '#8c1212',
    },
    secondary: {
      main: '#4caf50', // Success green
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

// Styled components
const PrudentialLogo = styled('div')({
  '& svg': {
    width: 120,
    height: 40,
  },
});

const AnimatedCircle = styled(motion.div)({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2px solid white',
});

export function AgentLanding() {
  const [selectedAccent, setSelectedAccent] = useState<string>('default');

  // Build URL with accent parameter if not default
  const getCallUrl = () => {
    const baseUrl = '/agent/roleplay?product=prushield';
    return selectedAccent !== 'default'
      ? `${baseUrl}&accent=${selectedAccent}`
      : baseUrl;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <PrudentialLogo>
                <svg
                  viewBox="0 0 100 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.0275 26.0274C7.06261 25.0954 4.97296 22.1116 4.97296 18.5821C4.97296 14.2132 8.51863 10.6676 12.8874 10.6676C16.417 10.6676 19.4008 12.7572 20.3327 15.7221"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M38.1177 23.0254H47.2368C47.9339 23.0254 48.5149 22.4642 48.5149 21.7473V14.0165C48.5149 10.2063 45.3756 7.06689 41.5653 7.06689H28.9986V21.7473C28.9986 22.4642 29.5599 23.0254 30.2767 23.0254H36.8396"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M36.8395 23.0254V17.6532C36.8395 17.0919 37.2848 16.6465 37.8461 16.6465H38.1175C38.6788 16.6465 39.1242 17.0919 39.1242 17.6532V23.0254"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M60.7407 23.0254H51.6216C50.9048 23.0254 50.3237 22.4642 50.3237 21.7473V14.0165C50.3237 10.2063 53.4631 7.06689 57.2732 7.06689H69.8399V21.7473C69.8399 22.4642 69.2786 23.0254 68.5618 23.0254H61.9994"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M61.9996 23.0254V17.6532C61.9996 17.0919 61.5541 16.6465 60.993 16.6465H60.7214C60.1603 16.6465 59.7148 17.0919 59.7148 17.6532V23.0254"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M82.4077 23.0254H73.2886C72.5718 23.0254 72.0105 22.4642 72.0105 21.7473V14.0165C72.0105 10.2063 75.1498 7.06689 78.9601 7.06689H91.5268V21.7473C91.5268 22.4642 90.9655 23.0254 90.2487 23.0254H83.6664"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M83.6665 23.0254V17.6532C83.6665 17.0919 83.2212 16.6465 82.6599 16.6465H82.3885C81.8272 16.6465 81.3818 17.0919 81.3818 17.6532V23.0254"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                  <path
                    d="M95.0723 7.06689H20.332V4.10205H95.0723V7.06689Z"
                    stroke="#C11919"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                  />
                </svg>
              </PrudentialLogo>
            </Box>

            <Typography variant="h5" align="center" gutterBottom>
              Health Insurance Consultation
            </Typography>

            <Card
              variant="outlined"
              sx={{
                bgcolor: 'grey.50',
                mb: 3,
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex' }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'error.light',
                      mr: 2,
                    }}
                  >
                    <Phone />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Alex Tan
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Prudential Financial Advisor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Alex specializes in helping individuals and families plan
                      for their healthcare protection with PRUShield plans.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Receive a call from an experienced financial advisor who can guide
              you through health insurance options tailored to your needs.
            </Typography>

            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="accent-select-label">Advisor Accent</InputLabel>
                <Select
                  labelId="accent-select-label"
                  id="accent-select"
                  value={selectedAccent}
                  label="Advisor Accent"
                  onChange={(e) => setSelectedAccent(e.target.value)}
                >
                  <MenuItem value="default">Default Accent</MenuItem>
                  <MenuItem value="british">British Accent</MenuItem>
                  <MenuItem value="irish">Irish Accent</MenuItem>
                </Select>
              </FormControl>

              <Button
                component={Link}
                to={getCallUrl()}
                fullWidth
                variant="contained"
                color="error"
                size="large"
                sx={{
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box sx={{ position: 'relative', mr: 1 }}>
                  <AnimatedCircle
                    animate={{
                      scale: [1, 1.8],
                      opacity: [1, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeOut',
                    }}
                  />
                  <Phone size={20} />
                </Box>
                Receive Call
              </Button>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{
                display: 'block',
                mt: 3,
                px: 2,
              }}
            >
              By proceeding, you'll be connected with our virtual advisor for a
              consultation about Prudential PRUShield health insurance products.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
